import { useCallback, useEffect, useState } from 'react';
import { App } from './App';
import { TgGate } from './components/TgGate';
import { bootstrapSession } from './lib/session';
import { getTgContext } from './lib/tg';

type GateState =
  | { kind: 'detecting' }
  | { kind: 'no-tg' }
  | { kind: 'auth-failed' }
  | { kind: 'ready' };

const TG_DETECT_TIMEOUT_MS = 1500;
const TG_POLL_INTERVAL_MS = 50;

function detectTg(signal: AbortSignal): Promise<boolean> {
  if (getTgContext()) return Promise.resolve(true);
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      if (signal.aborted) {
        window.clearInterval(id);
        resolve(false);
      } else if (getTgContext()) {
        window.clearInterval(id);
        resolve(true);
      } else if (Date.now() - started >= TG_DETECT_TIMEOUT_MS) {
        window.clearInterval(id);
        resolve(false);
      }
    }, TG_POLL_INTERVAL_MS);
    signal.addEventListener(
      'abort',
      () => {
        window.clearInterval(id);
        resolve(false);
      },
      { once: true },
    );
  });
}

export function AppShell() {
  const [gate, setGate] = useState<GateState>({ kind: 'detecting' });
  const [attemptId, setAttemptId] = useState(0);

  const retry = useCallback(() => setAttemptId((n) => n + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: attemptId is the retry trigger — bumping it re-runs bootstrap
  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    (async () => {
      setGate({ kind: 'detecting' });
      const hasTg = await detectTg(ctrl.signal);
      if (cancelled) return;
      if (!hasTg) {
        setGate({ kind: 'no-tg' });
        return;
      }
      const ctx = getTgContext();
      if (ctx) {
        ctx.webApp.ready();
        ctx.webApp.expand();
      }
      try {
        await bootstrapSession();
        if (cancelled) return;
        setGate({ kind: 'ready' });
      } catch (err) {
        if (cancelled) return;
        console.error('session_bootstrap_failed', err);
        setGate({ kind: 'auth-failed' });
      }
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [attemptId]);

  if (gate.kind === 'ready') return <App />;
  if (gate.kind === 'detecting') return <TgGate state="detecting" />;
  if (gate.kind === 'no-tg') return <TgGate state="no-tg" />;
  return <TgGate state="auth-failed" onRetry={retry} />;
}
